import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

/* ═══════════════════════════════════════════════════════════════════════════
   CASHFLOW RING — Animated ring showing cash position health
   ═══════════════════════════════════════════════════════════════════════════ */
function CashflowRing({ targetScore = 85, animate = false }: { targetScore?: number; animate?: boolean }) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let frame: number;
    const duration = 2500, start = performance.now();
    const tick = (now: number) => { const p = Math.min((now - start) / duration, 1); setDisplayScore(Math.round((1 - Math.pow(1 - p, 3)) * targetScore)); if (p < 1) frame = requestAnimationFrame(tick); };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, targetScore]);
  const c = 2 * Math.PI * 90;
  return (
    <div className="cashflow-ring">
      <svg width="220" height="220" viewBox="0 0 200 200">
        <defs><linearGradient id="capflowGradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#10b981" /><stop offset="50%" stopColor="#34d399" /><stop offset="100%" stopColor="#6ee7b7" /></linearGradient></defs>
        <circle className="ring-track" cx="100" cy="100" r="90" />
        <circle className="ring-fill" cx="100" cy="100" r="90" style={{ strokeDasharray: c, strokeDashoffset: animate ? c - (c * targetScore) / 100 : c, transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div className="cashflow-ring-value">
        <div className="stat-counter" style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff' }}>£{displayScore}K</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(52, 211, 153, 0.7)', fontWeight: 500, marginTop: 4 }}>Cash Position</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HMRC MTD DEMO — Shows automated VAT return flow
   ═══════════════════════════════════════════════════════════════════════════ */
function HMRCDemo() {
  const [phase, setPhase] = useState<'idle' | 'connecting' | 'calculating' | 'ready'>('idle');
  const ref = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const startDemo = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setPhase('connecting');
    setTimeout(() => setPhase('calculating'), 1500);
    setTimeout(() => setPhase('ready'), 3000);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setTimeout(startDemo, 400); }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startDemo]);

  const vatData = [
    { label: 'Output VAT (Sales)', amount: '£18,420.00', color: '#ef4444' },
    { label: 'Input VAT (Purchases)', amount: '£6,890.00', color: '#10b981' },
    { label: 'CIS Deductions Applied', amount: '-£2,340.00', color: '#f59e0b' },
    { label: 'Net VAT Payable', amount: '£9,190.00', color: '#fff', bold: true },
  ];

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div className="glass-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: '1px solid rgba(16,185,129,0.15)' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase === 'ready' ? '#10b981' : '#f59e0b' }} />
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>
            {phase === 'idle' ? 'HMRC MTD Integration' : phase === 'connecting' ? '🔗 Connecting to HMRC...' : phase === 'calculating' ? '🧮 Calculating VAT return...' : '✅ VAT Return Ready'}
          </span>
        </div>

        {phase === 'connecting' && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🏛️</div>
            <div style={{ fontSize: '1rem', color: '#34d399', fontWeight: 600 }}>Authenticating with HMRC OAuth2...</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8 }}>Fraud Prevention Headers ✓ · MTD Compliant ✓</div>
          </div>
        )}

        {phase === 'calculating' && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>📊</div>
            <div style={{ fontSize: '1rem', color: '#34d399', fontWeight: 600 }}>Scanning invoices & receipts...</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8 }}>142 transactions matched · 3 anomalies flagged</div>
          </div>
        )}

        {phase === 'ready' && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#34d399', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Q2 2025 VAT Return — Ready to Submit</div>
            {vatData.map((item, idx) => (
              <div key={item.label} className="ledger-entry" style={{ animationDelay: `${idx * 0.15}s`, display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, background: 'rgba(17, 22, 56, 0.4)', borderBottom: item.bold ? '2px solid rgba(16,185,129,0.3)' : undefined }}>
                <span style={{ fontSize: '0.9rem', color: '#e2e8f0', fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
                <span style={{ fontSize: '0.9rem', color: item.color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.amount}</span>
              </div>
            ))}
            <button style={{ marginTop: 16, padding: '12px', fontSize: '0.9rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              🏛️ Submit to HMRC — One Click
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STATS COUNTER
   ═══════════════════════════════════════════════════════════════════════════ */
function StatsCounter({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => { const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.5 }); if (ref.current) o.observe(ref.current); return () => o.disconnect(); }, []);
  useEffect(() => { if (!inView) return; let f: number; const d = 2000, s = performance.now(); const t = (n: number) => { const p = Math.min((n - s) / d, 1); setCount(Math.round((1 - Math.pow(1 - p, 3)) * end)); if (p < 1) f = requestAnimationFrame(t); }; f = requestAnimationFrame(t); return () => cancelAnimationFrame(f); }, [inView, end]);
  return <span ref={ref} className="stat-counter">{prefix}{count.toLocaleString()}{suffix}</span>;
}

const FEATURES = [
  { icon: '🏛️', title: 'HMRC MTD Integration', description: 'Direct Making Tax Digital connection. File VAT returns with one click. Fully compliant with Fraud Prevention Headers.', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { icon: '🏢', title: 'Companies House API', description: 'REST, Streaming, and OAuth2 integration. Auto-fill company data, verify directors, monitor filings.', gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { icon: '🏦', title: 'Open Banking Feeds', description: 'Connect bank accounts directly. Automated reconciliation with AI-powered transaction matching.', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { icon: '📄', title: 'Smart Invoicing', description: 'Create professional invoices in seconds. Auto-send, track payments, and chase overdue with AI reminders.', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
  { icon: '🔨', title: 'CIS Compliance', description: 'Subcontractor verification, deduction tracking (0%/20%/30%), CIS300 returns. Built for construction companies.', gradient: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { icon: '🤖', title: 'AI Cash Flow Forecasting', description: 'Predict cash position 30/60/90 days ahead. Early warnings on shortfalls with actionable recommendations.', gradient: 'linear-gradient(135deg, #ec4899, #f472b6)' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN LANDING
   ═══════════════════════════════════════════════════════════════════════════ */
export default function EFCapFlowLanding() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [heroInView, setHeroInView] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => { const h = () => setScrollY(window.scrollY); window.addEventListener('scroll', h, { passive: true }); return () => window.removeEventListener('scroll', h); }, []);
  useEffect(() => { const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeroInView(true); }, { threshold: 0.1 }); if (heroRef.current) o.observe(heroRef.current); return () => o.disconnect(); }, []);

  return (
    <div className="landing-root">
      {/* NAV */}
      <nav className={scrollY > 50 ? 'nav-glass' : ''} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transition: 'all 0.3s ease', ...(scrollY <= 50 ? { background: 'transparent' } : {}) }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #10b981, #34d399)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>💰</div>
            <div><span style={{ fontSize: '1.1rem', fontWeight: 700 }}>EF-CapFlow</span><span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 8 }}>by Executive Force AI</span></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#fff', background: '#10b981', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Start Free Trial</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section ref={heroRef} className="grid-pattern" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 1.5rem 3rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--1" /><div className="landing-orb landing-orb--2" /><div className="landing-orb landing-orb--3" /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.2s', marginBottom: 24 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(16,185,129,0.1)', color: '#34d399', border: '1px solid rgba(16,185,129,0.2)' }}>💰 UK Accounting + HMRC MTD</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 24, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.4s' }}>
            <span style={{ color: '#fff' }}>UK Finance</span><br /><span className="text-gradient-capflow">Made Simple</span>
          </h1>
          <p style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: '#94a3b8', maxWidth: 620, margin: '0 auto 3rem', lineHeight: 1.6, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 0.6s' }}>
            HMRC Making Tax Digital. Companies House. Open Banking. CIS. All in one <span style={{ color: '#6ee7b7', fontWeight: 600 }}>AI-powered</span> platform.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48, opacity: heroInView ? 1 : 0, transform: heroInView ? 'scale(1)' : 'scale(0.8)', transition: 'all 0.8s ease 0.8s' }}>
            <div className="animate-float"><CashflowRing targetScore={85} animate={heroInView} /></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 1.0s' }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial 🚀</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* HMRC MTD DEMO */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-flex', padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(16,185,129,0.1)', color: '#34d399', border: '1px solid rgba(16,185,129,0.2)', marginBottom: 16 }}>HMRC Direct Connection</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>File VAT Returns in <span className="text-gradient-capflow">One Click</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Direct HMRC Making Tax Digital integration with OAuth2 authentication, automated calculation, and one-click submission.</p>
          </div>
          <HMRCDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* UK COMPLIANCE BADGES */}
      <section style={{ position: 'relative', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Built for <span className="text-gradient-capflow">UK Compliance</span></h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, textAlign: 'center' }}>
            {[
              { icon: '🏛️', label: 'HMRC MTD', desc: 'VAT Compliant' },
              { icon: '🏢', label: 'Companies House', desc: 'Full API Access' },
              { icon: '🏦', label: 'Open Banking', desc: 'Bank Feeds' },
              { icon: '🔨', label: 'CIS', desc: 'Construction Scheme' },
              { icon: '🔒', label: 'GDPR', desc: 'Data Compliant' },
              { icon: '🇬🇧', label: 'UK Data', desc: 'UK/EU Hosted' },
            ].map((badge) => (
              <div key={badge.label} className="glass-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>{badge.icon}</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#34d399' }}>{badge.label}</div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>{badge.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* FEATURES */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Everything Your <span className="text-gradient-capflow">Accountant Wishes</span> They Had</h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>AI-powered accounting built specifically for UK businesses and construction companies.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="glass-card" style={{ padding: '1.5rem' }}>
                <div className="feature-icon-glow"><div style={{ width: 48, height: 48, borderRadius: 12, background: f.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: 16 }}>{f.icon}</div></div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: '0.9rem', color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* COMPETITOR TABLE */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 48 }}>How EF-CapFlow <span className="text-gradient-capflow">Compares</span></h2>
          <div className="glass-card" style={{ padding: '2rem', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(16,185,129,0.2)' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8' }}>Feature</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', color: '#34d399', fontWeight: 700 }}>EF-CapFlow</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>Xero</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>QuickBooks</th>
              </tr></thead>
              <tbody>
                {[
                  ['AI-Native', '✅', '❌', '❌'],
                  ['HMRC MTD Direct', '✅', '✅', '✅'],
                  ['Companies House API', '✅', '❌', '❌'],
                  ['CIS (Construction)', '✅', '💰', '💰'],
                  ['AI Cash Forecasting', '✅', '❌', '❌'],
                  ['Price', '£29/mo', 'From £15/mo', 'From £12/mo'],
                ].map(([f, ...v]) => (
                  <tr key={f} style={{ borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
                    <td style={{ padding: '12px 8px', color: '#e2e8f0', textAlign: 'left' }}>{f}</td>
                    {v.map((val, i) => <td key={i} style={{ padding: '12px 8px', textAlign: 'center', color: i === 0 ? '#fff' : '#94a3b8' }}>{val}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* STATS */}
      <section style={{ position: 'relative', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[{ value: 142, suffix: '+', label: 'VAT Returns Filed' }, { value: 85, suffix: '%', label: 'Time Saved' }, { value: 99, suffix: '.9%', label: 'HMRC Acceptance' }, { value: 24, suffix: 'hrs', label: 'Avg Refund Time' }].map((s) => (
            <div key={s.label}><div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}><StatsCounter end={s.value} suffix={s.suffix} /></div><div style={{ fontSize: '0.85rem', color: '#64748b' }}>{s.label}</div></div>
          ))}
        </div>
      </section>

      <div className="section-divider" />

      {/* PRICING */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Simple <span className="text-gradient-capflow">Pricing</span></h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: 40 }}>7-day free trial. No credit card required. Cancel anytime.</p>
          <div className="glass-card" style={{ padding: '2.5rem', borderColor: 'rgba(16,185,129,0.4)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#34d399', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>EF-CapFlow</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: '3.5rem', fontWeight: 800, color: '#fff' }}>£29</span><span style={{ fontSize: '1.1rem', color: '#64748b' }}>/month</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 24 }}>HMRC MTD · Companies House · Open Banking · CIS</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
              {['Direct HMRC MTD integration', 'Companies House REST + Streaming API', 'Open Banking bank feeds', 'CIS subcontractor management', 'AI cash flow forecasting', 'Automated VAT returns'].map((item) => (
                <li key={item} style={{ fontSize: '0.9rem', color: '#cbd5e1', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: '#34d399' }}>✓</span> {item}</li>
              ))}
            </ul>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* FINAL CTA */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--2" style={{ bottom: '20%', left: '10%' }} /><div className="landing-orb landing-orb--3" style={{ top: '10%', right: '10%' }} /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, marginBottom: 24 }}>Ready to <span className="text-gradient-capflow">Simplify UK Finance</span>?</h2>
          <p style={{ fontSize: '1.2rem', color: '#94a3b8', marginBottom: 40 }}>Join UK businesses filing VAT returns in one click instead of spending hours on spreadsheets.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start Free Trial →</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: '1px solid rgba(16,185,129,0.15)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg, #10b981, #34d399)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>💰</div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Executive Force AI</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#475569', margin: 0 }}>© {new Date().getFullYear()} Executive Force AI Limited. All rights reserved.</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <button onClick={() => navigate('/login')} style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <a href="https://executiveforceai.co.uk/privacy-policy" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Privacy</a>
            <a href="https://executiveforceai.co.uk/terms-conditions" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
